<template>
  <v-container
    class="pa-1"
    fluid
  >
    <v-row dense>
      <v-col
        v-if="!useDeliveryLayout"
        class="order-2 order-sm-1"
        cols="12"
        sm="5"
      >
        <v-card class="fill-height pa-1">
          <num-pad
            v-model="numPadModel"
            :button-height="appHasFiscalDocsProvider && appHasFiscalDocsEnabled ? 55 : 70"
            :disabled="numPadDisabled"
            :initial-value="initValue"
            :max-num-value="maxTransactionAmount"
            :small="$vuetify.breakpoint.xsOnly"
            class="fill-height align-content-space-between"
            currency
            @numpad:enter="onNumPadEnter"
          >
            <div class="px-1">
              <div class="mb-1 d-flex justify-space-between">
                <v-btn
                  :disabled="selectedPaymentType !== PaymentTypeEnum.CASH || !isValidAmountForCashPayment"
                  class="flex-grow-1 mr-1"
                  color="orange"
                  dark
                  style="min-width: 24px"
                  @click="setNumPadValue(5)"
                >
                  5€
                </v-btn>

                <v-btn
                  :disabled="selectedPaymentType !== PaymentTypeEnum.CASH || !isValidAmountForCashPayment"
                  class="flex-grow-1 mr-1"
                  color="orange"
                  dark
                  style="min-width: 24px"
                  @click="setNumPadValue(10)"
                >
                  10€
                </v-btn>

                <v-btn
                  :disabled="selectedPaymentType !== PaymentTypeEnum.CASH || !isValidAmountForCashPayment"
                  class="flex-grow-1 mr-1"
                  color="orange"
                  dark
                  style="min-width: 24px"
                  @click="setNumPadValue(20)"
                >
                  20€
                </v-btn>

                <v-btn
                  :disabled="selectedPaymentType !== PaymentTypeEnum.CASH || !isValidAmountForCashPayment"
                  class="flex-grow-1 mr-1"
                  color="orange"
                  dark
                  style="min-width: 24px"
                  @click="setNumPadValue(50)"
                >
                  50€
                </v-btn>

                <v-btn
                  :disabled="selectedPaymentType !== PaymentTypeEnum.CASH || !isValidAmountForCashPayment"
                  class="flex-grow-1"
                  color="orange"
                  dark
                  style="min-width: 24px"
                  @click="setNumPadValue(100)"
                >
                  100€
                </v-btn>
              </div>

              <template
                v-if="appHasFiscalDocsEnabled && receipt && (appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT) || appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE))"
              >
                <div>
                  <v-btn-toggle
                    v-model="selectedFiscalDocType"
                    class="lighten-2 elevation-0 ma-0 mb-1 d-flex"
                    mandatory
                  >
                    <v-btn
                      v-if="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT)"
                      :key="`${activeReceiptType}`"
                      :class="[FiscalDocTypeEnum.isReceipt(selectedFiscalDocType) ? 'flex-grow-1' : 'flex-grow-0', {'pr-0': appHasReceiptSaleDocEnabled && FiscalDocTypeEnum.isReceipt(selectedFiscalDocType)}]"
                      :disabled="receiptDocumentDisabled"
                      :style="FiscalDocTypeEnum.isReceipt(selectedFiscalDocType) ? 'opacity: 1;' : 'opacity: 0.35;'"
                      :value="activeReceiptType"
                      class="elevation-0 white--text"
                      color="green"
                      style="height: 40px; border: 0;"
                      @click="onSelectedPaymentTypeChange(activeReceiptType)"
                    >
                      <v-icon
                        color="white"
                        size="18"
                      >
                        mdi-receipt-text-plus-outline
                      </v-icon>

                      <template v-if="FiscalDocTypeEnum.isReceipt(selectedFiscalDocType)">
                        <span class="ml-2">
                          {{ selectedFiscalDocTypeName | truncate(15) }}
                        </span>

                        <template
                          v-if="appHasReceiptSaleDocEnabled && (appHasReceiptSelfSupplyDocEnabled || appHasReceiptSelfUseDocEnabled || appHasReceiptServiceDocEnabled || appHasReceiptCollectionDocEnabled)"
                        >
                          <v-spacer />

                          <v-menu offset-y>
                            <template #activator="{on}">
                              <v-btn
                                :disabled="receiptDocumentDisabled"
                                :value="activeReceiptType"
                                class="px-0"
                                color="green darken-1"
                                depressed
                                height="40"
                                min-width="40"
                                v-on="on"
                              >
                                <v-icon
                                  class="float-end"
                                  color="white"
                                >
                                  mdi-chevron-down
                                </v-icon>
                              </v-btn>
                            </template>

                            <v-list>
                              <v-list-item
                                v-if="appHasReceiptSaleDocEnabled"
                                @click="() => {
                                  activeReceiptType = FiscalDocTypeEnum.RECEIPT
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.RECEIPT)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.Receipt') }}</v-list-item-title>
                              </v-list-item>

                              <v-list-item
                                v-if="appHasReceiptServiceDocEnabled"
                                @click="() => {
                                  activeReceiptType = FiscalDocTypeEnum.SERVICE_RECEIPT
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.SERVICE_RECEIPT)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.ServiceReceipt') }}</v-list-item-title>
                              </v-list-item>

                              <v-list-item
                                v-if="appHasReceiptSelfSupplyDocEnabled"
                                @click="() => {
                                  activeReceiptType = FiscalDocTypeEnum.RECEIPT_SELF_SUPPLY
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.RECEIPT_SELF_SUPPLY)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.SelfSupplyReceipt') }}</v-list-item-title>
                              </v-list-item>

                              <v-list-item
                                v-if="appHasReceiptSelfUseDocEnabled"
                                @click="() => {
                                  activeReceiptType = FiscalDocTypeEnum.RECEIPT_SELF_USE
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.RECEIPT_SELF_USE)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.SelfUseReceipt') }}</v-list-item-title>
                              </v-list-item>

                              <v-list-item
                                v-if="appHasReceiptCollectionDocEnabled"
                                @click="() => {
                                  activeReceiptType = FiscalDocTypeEnum.COLLECTION_RECEIPT
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.COLLECTION_RECEIPT)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.CollectionReceipt') }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </template>
                      </template>
                    </v-btn>

                    <v-btn
                      v-if="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE)"
                      :key="`${activeInvoiceType}`"
                      :class="[FiscalDocTypeEnum.isInvoice(selectedFiscalDocType) ? 'flex-grow-1' : 'flex-grow-0', {'pr-0': appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled && FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)}]"
                      :disabled="invoiceDocumentDisabled"
                      :style="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType) ? 'opacity: 1;' : 'opacity: 0.35;'"
                      :value="activeInvoiceType"
                      class="elevation-0 white--text"
                      color="blue"
                      style="height: 40px; border: 0;"
                      @click="onSelectedPaymentTypeChange(activeInvoiceType)"
                    >
                      <div v-if="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)">
                        <v-badge
                          :color="selectedReceiptData ? 'green darken-1' : 'red'"
                          :icon="selectedReceiptData ? 'mdi-check-bold' : 'mdi-exclamation-thick'"
                          :value="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)"
                          class="invoice-warning-badge"
                          inline
                        />
                      </div>

                      <v-icon
                        color="white"
                        size="18"
                      >
                        mdi-invoice-text-plus-outline
                      </v-icon>

                      <template v-if="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)">
                        <span class="ml-2">
                          {{
                            appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled ? selectedFiscalDocType === FiscalDocTypeEnum.INVOICE ? $t('Account.Billing.Field.IsReceiptToggle.InvoiceSale') : $t('Account.Billing.Field.IsReceiptToggle.InvoiceService') : $t('Account.Billing.Field.IsReceiptToggle.Invoice')
                          }}
                        </span>

                        <template v-if="appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled">
                          <v-spacer />

                          <v-menu offset-y>
                            <template #activator="{on}">
                              <v-btn
                                :value="activeInvoiceType"
                                class="px-0"
                                color="blue darken-1"
                                depressed
                                height="40"
                                min-width="40"
                                v-on="on"
                              >
                                <v-icon class="float-end">
                                  mdi-chevron-down
                                </v-icon>
                              </v-btn>
                            </template>


                            <v-list>
                              <v-list-item
                                v-if="appHasInvoiceSaleDocEnabled"
                                @click="() => {
                                  activeInvoiceType = FiscalDocTypeEnum.INVOICE
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.INVOICE)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.Invoice') }}</v-list-item-title>
                              </v-list-item>

                              <v-list-item
                                v-if="appHasInvoiceServiceDocEnabled"
                                @click="() => {
                                  activeInvoiceType = FiscalDocTypeEnum.SERVICE_INVOICE
                                  onSelectedPaymentTypeChange(FiscalDocTypeEnum.SERVICE_INVOICE)
                                }"
                              >
                                <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.ServiceInvoice') }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </template>
                      </template>
                    </v-btn>

                    <v-btn
                      v-if="!useDeliveryLayout && appConfig?.LOCATION_DATA?.AllowWithNoDocument"
                      :class="selectedFiscalDocType === FiscalDocTypeEnum.NONE ? 'flex-grow-1' : 'flex-grow-0'"
                      :disabled="noFiscalDocumentDisabled"
                      :style="selectedFiscalDocType === FiscalDocTypeEnum.NONE ? 'opacity: 1;' : 'opacity: 0.35;'"
                      :value="FiscalDocTypeEnum.NONE"
                      class="elevation-0 white--text"
                      color="purple"
                      style="height: 40px; border: 0;"
                      @click="onSelectedPaymentTypeChange(FiscalDocTypeEnum.NONE)"
                    >
                      <v-icon
                        color="white"
                        size="18"
                      >
                        mdi-receipt-text-remove
                      </v-icon>

                      <template v-if="selectedFiscalDocType === FiscalDocTypeEnum.NONE">
                        <span class="ml-2">
                          {{ $t('Account.Billing.Field.IsReceiptToggle.None') }}
                        </span>
                      </template>
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <div>
                  <v-btn-toggle
                    v-model="selectedReceiptNotify"
                    :disabled="selectedReceiptNotifyDisabled"
                    class="elevation-0 ma-0-f d-flex"
                    color="success"
                    dense
                    multiple
                  >
                    <v-btn
                      :color="selectedReceiptNotify.includes(NotifyTypeEnum.PRINT) ? 'success' : ''"
                      :disabled="selectedReceiptNotifyDisabled"
                      class="flex-grow-1"
                      text
                      value="print"
                    >
                      <v-icon
                        color="success"
                        left
                        size="22"
                      >
                        mdi-printer-pos-outline
                      </v-icon>

                      {{ $t('Settings.Documents.OrderReceipt.Notify.Print').toLocaleUpperCase($i18n.locale) }}
                    </v-btn>

                    <v-btn
                      :color="selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL) ? 'primary' : ''"
                      :disabled="selectedReceiptNotifyDisabled || !appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)"
                      class="flex-grow-1 justify-start"
                      text
                      value="email"
                    >
                      <div class="flex-grow-1 mt-1">
                        <v-icon
                          class="mb-1"
                          color="primary"
                          left
                          size="18"
                        >
                          mdi-email-arrow-right-outline
                        </v-icon>

                        {{ $t('Settings.Documents.OrderReceipt.Notify.Email').toLocaleUpperCase($i18n.locale) }}
                        ({{ selectedReceiptNotifyEmails.length > 0 ? selectedReceiptNotifyEmails.length : hasCustomerWithEmail ? 1 : 0 }})
                      </div>

                      <div
                        v-if="selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL)"
                        class="mt-auto"
                      >
                        <v-btn
                          :disabled="!selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL)"
                          icon
                          small
                          @click.stop="showEmailAddressesDialog"
                        >
                          <v-icon>
                            mdi-plus-box
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </template>
            </div>
          </num-pad>
        </v-card>
      </v-col>

      <v-col
        :sm="useDeliveryLayout ? 12 : 7"
        class="order-1 order-sm-2"
        cols="12"
      >
        <v-card class="fill-height">
          <v-toolbar
            color="light-blue"
            dark
            flat
            height="50"
          >
            <v-toolbar-title>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Title') }}</v-toolbar-title>

            <v-spacer />
          </v-toolbar>

          <v-card-text
            :style="receipt ? useDeliveryLayout ? 'max-height: 100%; min-height: 150px;' : 'max-height: 300px; min-height: 215px;' : useDeliveryLayout ? 'max-height: 100%; min-height: 150px;' : (appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) ? 'max-height: 300px; min-height: 135px;' : 'max-height: 300px; min-height: 215px;'"
            class="pa-0 px-2"
            style="overflow: auto;"
          >
            <template v-if="!cashTransactionAllowed && !hasCashTransaction && selectedPaymentType === PaymentTypeEnum.CASH">
              <v-alert
                class="text-caption ma-0 mt-1 pa-0 rounded"
                color="error"
                dark
                dense
                tile
              >
                <template #prepend>
                  <v-avatar
                    class="mr-2 ma-1 darken-2 rounded"
                    color="error"
                    height="22"
                    min-height="22"
                    min-width="22"
                    tile
                    width="22"
                  >
                    <v-icon
                      :size="16"
                      dark
                    >
                      mdi-alert
                    </v-icon>
                  </v-avatar>
                </template>

                <div
                  v-safe-html="$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.CashWarning', {amount: maxCashPaymentAmount})"
                  class="py-1"
                  style="line-height: 14px;"
                />
              </v-alert>
            </template>

            <template v-if="!isValidAmountForSelfDeliveryReceipt && FiscalDocTypeEnum.isSelfDoc(selectedFiscalDocType)">
              <v-alert
                class="text-caption ma-0 mt-1 pa-0 rounded"
                color="error"
                dark
                dense
                tile
              >
                <template #prepend>
                  <v-avatar
                    class="mr-2 ma-1 darken-2 rounded"
                    color="error"
                    height="22"
                    min-height="22"
                    min-width="22"
                    tile
                    width="22"
                  >
                    <v-icon
                      :size="16"
                      dark
                    >
                      mdi-alert
                    </v-icon>
                  </v-avatar>
                </template>

                <div
                  v-safe-html="$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SelfDeliveryTotalWarning')"
                  class="py-1"
                  style="line-height: 14px;"
                />
              </v-alert>
            </template>

            <template v-if="hasNoSelfDeliveryTransaction && FiscalDocTypeEnum.isSelfDoc(selectedFiscalDocType)">
              <v-alert
                class="text-caption ma-0 mt-1 pa-0 rounded"
                color="error"
                dark
                dense
                tile
              >
                <template #prepend>
                  <v-avatar
                    class="mr-2 ma-1 darken-2 rounded"
                    color="error"
                    height="22"
                    min-height="22"
                    min-width="22"
                    tile
                    width="22"
                  >
                    <v-icon
                      :size="16"
                      dark
                    >
                      mdi-alert
                    </v-icon>
                  </v-avatar>
                </template>

                <div
                  v-safe-html="$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SelfDeliveryWarning')"
                  class="py-1"
                  style="line-height: 14px;"
                />
              </v-alert>
            </template>

            <v-list
              class="pb-0"
              dense
            >
              <template v-for="(transaction, index) in transactions">
                <template v-if="isEftPosEnabledTransaction(transaction) && appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                  <div :key="`transaction-info-${index}`">
                    <v-alert
                      :color="getTransactionInfo(transaction)?.Color"
                      class="text-caption ma-0 pa-0 rounded-t"
                      dark
                      dense
                      tile
                    >
                      <template #prepend>
                        <v-avatar
                          :color="getTransactionInfo(transaction)?.Color"
                          class="mr-2 ma-1 darken-2 rounded"
                          height="22"
                          min-height="22"
                          min-width="22"
                          tile
                          width="22"
                        >
                          <v-icon
                            :size="16"
                            dark
                          >
                            {{ getTransactionInfo(transaction)?.Icon }}
                          </v-icon>
                        </v-avatar>
                      </template>

                      <template #append>
                        <template v-if="canManualAcceptCardTransaction(transaction) && !isManualAcceptedTransaction(transaction)">
                          <v-tooltip
                            bottom
                            color="deep-orange"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="transaction.busy"
                                class="deep-orange ma-1"
                                icon
                                ripple
                                style="width: 24px; height: 24px;"
                                v-on="on"
                                @click.stop="showConfirmManualAcceptCardTransactionDialog(transaction)"
                              >
                                <v-icon size="18">
                                  mdi-account-check
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAccept.Title') }}</span>
                          </v-tooltip>
                        </template>

                        <template v-if="transaction?.WaitingPos">
                          <v-progress-circular
                            class="ma-1"
                            color="white"
                            indeterminate
                            size="18"
                            width="2"
                          />
                        </template>

                        <template v-if="isManualAcceptedTransaction(transaction)">
                          <v-icon class="mx-1">
                            mdi-account-check
                          </v-icon>

                          <v-tooltip
                            bottom
                            color="deep-orange"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="transaction.busy"
                                class="deep-orange ma-1"
                                icon
                                ripple
                                style="width: 24px; height: 24px;"
                                v-on="on"
                                @click.stop="showConfirmManualAcceptCardTransactionDialog(transaction)"
                              >
                                <v-icon size="18">
                                  mdi-account-arrow-left
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAcceptRevert.Title') }}</span>
                          </v-tooltip>
                        </template>
                      </template>

                      <div
                        v-safe-html="getTransactionInfo(transaction)?.Message"
                        class="py-1"
                        style="line-height: 14px;"
                      />
                    </v-alert>

                    <v-divider :class="[getTransactionInfo(transaction)?.Color, isDarkMode ? 'darken-2' : 'lighten-4']" />
                  </div>
                </template>

                <template v-if="!cashTransactionAllowed && transaction.PaymentType === PaymentTypeEnum.CASH">
                  <div :key="`transaction-cash-warning-${index}`">
                    <v-alert
                      class="text-caption ma-0 pa-0 rounded-t"
                      color="error"
                      dark
                      dense
                      tile
                    >
                      <template #prepend>
                        <v-avatar
                          class="mr-2 ma-1 darken-2 rounded"
                          color="error"
                          height="22"
                          min-height="22"
                          min-width="22"
                          tile
                          width="22"
                        >
                          <v-icon
                            :size="16"
                            dark
                          >
                            mdi-alert
                          </v-icon>
                        </v-avatar>
                      </template>

                      <div
                        v-safe-html="$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.CashWarning', {amount: maxCashPaymentAmount})"
                        class="py-1"
                        style="line-height: 14px;"
                      />
                    </v-alert>

                    <v-divider :class="[isDarkMode ? 'warning darken-2' : 'warning lighten-4']" />
                  </div>
                </template>

                <v-progress-linear
                  v-if="transaction.WaitingPos || transaction.busy"
                  :key="`transaction-progress-${index}`"
                  height="2"
                  indeterminate
                />

                <v-list-item
                  :key="`transaction-amount-${index}`"
                  :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-3', 'ma-0', transaction.PaymentType === PaymentTypeEnum.CASH && cashTransactionAllowed ? 'rounded' : 'rounded-b']"
                  :disabled="loading || transaction.busy"
                  ripple
                  style="min-height: 56px;"
                  @click.stop="executeDefaultAction(transaction)"
                >
                  <v-badge
                    :color="eftPosCardTransactionPaidSuccessful(transaction) || transaction.PaymentType === PaymentTypeEnum.CASH || (appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.NONE || appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.LOCAL) ? 'success' : transaction.Paid === 'fail' ? 'error' : 'blue'"
                    :offset-x="26"
                    :offset-y="20"
                    overlap
                    right
                  >
                    <v-list-item-avatar
                      :color="getPaymentTypeByCode(transaction.PaymentType)?.color1"
                      class="ml-0"
                      size="30"
                    >
                      <img
                        v-if="!!getPaymentTypeByCode(transaction.PaymentType)?.image"
                        :src="`${appConfig.LOCATION_DATA.CdnUrl}assets/payments/${getPaymentTypeByCode(transaction.PaymentType)?.image}`"
                        style="height: 16px; width: auto;"
                        width="auto"
                      >
                      <v-icon
                        v-else
                        dark
                        small
                      >
                        {{ getPaymentTypeByCode(transaction.PaymentType)?.icon }}
                      </v-icon>
                    </v-list-item-avatar>

                    <template #badge>
                      <v-icon
                        v-if="eftPosCardTransactionPaidSuccessful(transaction) || !isEftPosEnabledTransaction(transaction) || (appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.NONE || appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.LOCAL)"
                      >
                        mdi-check
                      </v-icon>

                      <v-icon v-else-if="transaction.Paid === 'fail'">
                        mdi-exclamation-thick
                      </v-icon>

                      <v-icon v-else>
                        mdi-plus
                      </v-icon>
                    </template>
                  </v-badge>

                  <v-list-item-content>
                    <v-list-item-title class="my-1 text-body-2 text-sm-body-1">
                      <span class="font-weight-bold">
                        {{ getTransactionPaymentTypeName(transaction) }}
                      </span>

                      <div
                        v-if="transaction.PaymentType === PaymentTypeEnum.CASH && !useDeliveryLayout"
                        class="orange--text mt-1"
                      >
                        {{ transaction.Received | currency }}
                      </div>

                      <template v-if="!!transaction?.Data?.Instalments">
                        <span class="px-1">-</span>
                        <span class="text-caption d-inline-flex grey--text text--lighten-1">
                          {{ transaction.Data.Instalments }} {{ $t('Restaurant.Tables.Dialog.SelectInstalments.TitleSmall') }}
                        </span>
                      </template>

                      <template v-if="useDeliveryLayout">
                        <div
                          v-if="!PaymentTypeEnum.isEftPosPaymentType(transaction.PaymentType) && (!PaymentTypeEnum.isDeliveryCard(transaction.PaymentType) || (PaymentTypeEnum.isDeliveryCard(transaction.PaymentType) && (!transaction.Paid || transaction.Paid !== 'success'))) || (PaymentTypeEnum.isEftPosPaymentType(transaction.PaymentType) && (!transaction.Paid || transaction.Paid !== 'success'))"
                          class="mt-2"
                        >
                          <v-tooltip
                            bottom
                            color="orange"
                            open-delay="750"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="loading || transaction.WaitingPos || transaction.busy"
                                class="px-1"
                                color="orange"
                                dark
                                ripple
                                x-small
                                v-on="on"
                                @click.stop="deliverySelectPaymentMethod(transaction)"
                              >
                                {{ $t('Common.Button.ChangePayment') }}
                              </v-btn>
                            </template>

                            <span>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Button.Terminal.ChangePaymentType') }}</span>
                          </v-tooltip>
                        </div>
                      </template>
                    </v-list-item-title>

                    <v-list-item-subtitle v-if="isEftPosEnabledTransaction(transaction) && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD && transaction.Nickname">
                      {{ transaction.Nickname }}
                      <span class="text-caption grey--text text-capitalize">({{ transaction.TerminalName }})</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <template v-if="appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD">
                      <template v-if="isEftPosEnabledTransaction(transaction)">
                        <template v-if="!transaction.Paid">
                          <v-tooltip
                            bottom
                            color="blue"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="transaction.WaitingPos || loading || transaction.busy"
                                class="blue--text"
                                icon
                                ripple
                                small
                                v-on="on"
                                @click.stop="showSelectPaymentTerminalDialog(transaction)"
                              >
                                <v-icon size="20">
                                  mdi-credit-card-plus
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Button.Terminal.Add') }}</span>
                          </v-tooltip>
                        </template>

                        <template v-if="transaction.Paid === 'fail'">
                          <v-tooltip
                            bottom
                            color="orange"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="transaction.WaitingPos || loading || transaction.busy || !transaction.Data"
                                class="orange--text"
                                icon
                                ripple
                                small
                                v-on="on"
                                @click.stop="onRetryTransaction(transaction)"
                              >
                                <v-icon size="20">
                                  mdi-credit-card-sync
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Button.Terminal.Retry') }}</span>
                          </v-tooltip>
                        </template>

                        <template
                          v-if="(PaymentTypeEnum.isCard(transaction.PaymentType) && eftPosCardTransactionPaidSuccessful(transaction) && !useDeliveryLayout) || (PaymentTypeEnum.isDeliveryCard(transaction.PaymentType) && eftPosCardTransactionPaidSuccessful(transaction) && useDeliveryLayout && posUser.Settings?.DeliveryStaff?.Permissions?.CanTransactionRefund)"
                        >
                          <v-tooltip
                            bottom
                            color="purple"
                          >
                            <template #activator="{on}">
                              <v-btn
                                :disabled="transaction.WaitingPos || loading || transaction.busy || !transaction.OrderCode"
                                class="purple--text"
                                icon
                                ripple
                                small
                                v-on="on"
                                @click.stop="showConfirmRefundTransactionDialog(transaction)"
                              >
                                <v-icon size="20">
                                  mdi-credit-card-remove
                                </v-icon>
                              </v-btn>
                            </template>

                            <span>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Button.Terminal.Refund') }}</span>
                          </v-tooltip>
                        </template>
                      </template>
                    </template>

                    <template v-if="canRemoveTransaction(transaction) && !useDeliveryLayout">
                      <v-tooltip
                        bottom
                        color="red"
                      >
                        <template #activator="{on}">
                          <v-btn
                            :class="isDarkMode ? 'red--text text--lighten-2' : 'red--text'"
                            :disabled="loading || transaction.busy"
                            icon
                            ripple
                            small
                            v-on="on"
                            @click.stop="onRemoveTransaction(transaction)"
                          >
                            <v-icon size="20">
                              close
                            </v-icon>
                          </v-btn>
                        </template>

                        <span>{{ $t('Restaurant.Tables.Dialog.Payment.Transactions.Button.Remove') }}</span>
                      </v-tooltip>
                    </template>
                  </v-list-item-action>

                  <v-list-item-action>
                    <v-list-item-action-text
                      class="text-end font-weight-bold text-body-1"
                      style="min-width: 75px;"
                    >
                      -{{ transaction.Amount | currency }}
                    </v-list-item-action-text>
                  </v-list-item-action>
                </v-list-item>

                <v-divider
                  v-if="index < transactions.length-1"
                  :key="`transaction-divider-${index}`"
                  class="my-1"
                />
              </template>
            </v-list>
          </v-card-text>

          <v-card-actions class="grey--text text--darken-2 pt-0 pb-2 px-2">
            <div style="width: 100%;">
              <v-divider class="my-1" />

              <div :class="[useDeliveryLayout ? 'mt-2' : 'mb-2']">
                <div
                  :class="$vuetify.theme.dark ? 'grey--text' : 'black--text'"
                  class="pr-1 text-body-1"
                >
                  <span class="font-weight-bold">{{ $t('Restaurant.Tables.Dialog.Payment.Total') }}:</span>
                  <span class="font-weight-bold float-right">{{ orderTotal | currency }}</span>
                </div>

                <template v-if="!useDeliveryLayout">
                  <div
                    :class="[orderChange > 0 ? 'info--text' : '']"
                    class="pr-1 text-body-1"
                  >
                    <span class="font-weight-bold">{{ $t('Restaurant.Tables.Dialog.Payment.Change') }}:</span>
                    <span class="font-weight-bold float-right">{{ orderChange | currency }}</span>
                  </div>

                  <div
                    :class="[tablePaidBalance === 0 ? '' : 'orange--text']"
                    class="pr-1 text-body-1"
                  >
                    <span class="font-weight-bold">{{ $t('Restaurant.Tables.Dialog.Payment.Balance') }}:</span>
                    <span class="font-weight-bold float-right">{{ tablePaidBalance | currency }}</span>
                  </div>
                </template>
              </div>

              <template v-if="!useDeliveryLayout">
                <payment-method-selector
                  v-model="selectedPaymentType"
                  :height="40"
                  :payment-methods="paymentTypesForPos"
                  class="d-flex"
                  shrink
                />

                <div>
                  <v-btn
                    :disabled="!canAddTransaction || loading"
                    :loading="loading"
                    block
                    class="green darken-1 white--text mt-1 font-weight-bold elevation-0"
                    style="height: 40px;"
                    @click="onAddTransaction"
                  >
                    {{ $t('Common.Button.TransactionAdd') }}
                    <v-spacer />
                    {{ numPadModel | currency }}
                  </v-btn>
                </div>
              </template>

              <template v-else>
                <div
                  v-if="appHasFiscalDocsEnabled && receipt && (appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT) || appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE))"
                  class="mt-4"
                >
                  <div v-if="!hasFiscalDocument">
                    <v-btn-toggle
                      v-model="selectedFiscalDocType"
                      class="lighten-2 elevation-0 ma-0 mb-1 d-flex"
                      mandatory
                    >
                      <v-btn
                        v-if="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT)"
                        :key="`${activeReceiptType}`"
                        :class="[FiscalDocTypeEnum.isReceipt(selectedFiscalDocType) ? 'flex-grow-1' : 'flex-grow-0', {'pr-0': appHasReceiptSaleDocEnabled && appHasReceiptServiceDocEnabled && FiscalDocTypeEnum.isReceipt(selectedFiscalDocType)}]"
                        :disabled="receiptDocumentDisabled"
                        :style="FiscalDocTypeEnum.isReceipt(selectedFiscalDocType) ? 'opacity: 1;' : 'opacity: 0.35;'"
                        :value="activeReceiptType"
                        class="elevation-0 white--text"
                        color="green"
                        style="height: 40px; border: 0;"
                        @click="onSelectedPaymentTypeChange(activeReceiptType)"
                      >
                        <v-icon
                          color="white"
                          size="18"
                        >
                          mdi-receipt-text-plus-outline
                        </v-icon>

                        <template v-if="FiscalDocTypeEnum.isReceipt(selectedFiscalDocType)">
                          <span class="ml-2">
                            {{
                              appHasReceiptSaleDocEnabled && appHasReceiptServiceDocEnabled ? selectedFiscalDocType === FiscalDocTypeEnum.RECEIPT ? $t('Account.Billing.Field.IsReceiptToggle.ReceiptSale') : $t('Account.Billing.Field.IsReceiptToggle.ReceiptService') : $t('Account.Billing.Field.IsReceiptToggle.Receipt')
                            }}
                          </span>

                          <template v-if="appHasReceiptSaleDocEnabled && appHasReceiptServiceDocEnabled">
                            <v-spacer />

                            <v-menu offset-y>
                              <template #activator="{on}">
                                <v-btn
                                  :value="activeReceiptType"
                                  class="px-0"
                                  color="green darken-1"
                                  depressed
                                  height="40"
                                  min-width="40"
                                  v-on="on"
                                >
                                  <v-icon
                                    class="float-end white--text"
                                  >
                                    mdi-chevron-down
                                  </v-icon>
                                </v-btn>
                              </template>


                              <v-list>
                                <v-list-item
                                  v-if="appHasReceiptSaleDocEnabled"
                                  @click="() => {
                                    activeReceiptType = FiscalDocTypeEnum.RECEIPT
                                    onSelectedPaymentTypeChange(FiscalDocTypeEnum.RECEIPT)
                                  }"
                                >
                                  <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.Receipt') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                  v-if="appHasReceiptServiceDocEnabled"
                                  @click="() => {
                                    activeReceiptType = FiscalDocTypeEnum.SERVICE_RECEIPT
                                    onSelectedPaymentTypeChange(FiscalDocTypeEnum.SERVICE_RECEIPT)
                                  }"
                                >
                                  <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.ServiceReceipt') }}</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </template>
                        </template>
                      </v-btn>

                      <v-btn
                        v-if="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE)"
                        :key="`${activeInvoiceType}`"
                        :class="[FiscalDocTypeEnum.isInvoice(selectedFiscalDocType) ? 'flex-grow-1' : 'flex-grow-0', {'pr-0': appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled && FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)}]"
                        :disabled="invoiceDocumentDisabled"
                        :style="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType) ? 'opacity: 1;' : 'opacity: 0.35;'"
                        :value="activeInvoiceType"
                        class="elevation-0 white--text"
                        color="blue"
                        style="height: 40px; border: 0;"
                        @click="onSelectedPaymentTypeChange(activeInvoiceType)"
                      >
                        <div v-if="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)">
                          <v-badge
                            :color="selectedReceiptData ? 'green darken-1' : 'red'"
                            :icon="selectedReceiptData ? 'mdi-check-bold' : 'mdi-exclamation-thick'"
                            :value="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)"
                            class="invoice-warning-badge"
                            inline
                          />
                        </div>

                        <v-icon
                          color="white"
                          size="18"
                        >
                          mdi-invoice-text-plus-outline
                        </v-icon>

                        <template v-if="FiscalDocTypeEnum.isInvoice(selectedFiscalDocType)">
                          <span class="ml-2">
                            {{
                              appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled ? selectedFiscalDocType === FiscalDocTypeEnum.INVOICE ? $t('Account.Billing.Field.IsReceiptToggle.InvoiceSale') : $t('Account.Billing.Field.IsReceiptToggle.InvoiceService') : $t('Account.Billing.Field.IsReceiptToggle.Invoice')
                            }}
                          </span>

                          <template v-if="appHasInvoiceSaleDocEnabled && appHasInvoiceServiceDocEnabled">
                            <v-spacer />

                            <v-menu offset-y>
                              <template #activator="{on}">
                                <v-btn
                                  :value="activeInvoiceType"
                                  class="px-0"
                                  color="blue darken-1"
                                  depressed
                                  height="40"
                                  min-width="40"
                                  v-on="on"
                                >
                                  <v-icon class="float-end">
                                    mdi-chevron-down
                                  </v-icon>
                                </v-btn>
                              </template>


                              <v-list>
                                <v-list-item
                                  v-if="appHasInvoiceSaleDocEnabled"
                                  @click="() => {
                                    activeInvoiceType = FiscalDocTypeEnum.INVOICE
                                    onSelectedPaymentTypeChange(FiscalDocTypeEnum.INVOICE)
                                  }"
                                >
                                  <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.Invoice') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                  v-if="appHasInvoiceServiceDocEnabled"
                                  @click="() => {
                                    activeInvoiceType = FiscalDocTypeEnum.SERVICE_INVOICE
                                    onSelectedPaymentTypeChange(FiscalDocTypeEnum.SERVICE_INVOICE)
                                  }"
                                >
                                  <v-list-item-title>{{ $t('Settings.Documents.OrderReceipt.Document.ServiceInvoice') }}</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </template>
                        </template>
                      </v-btn>

                      <v-btn
                        v-if="!useDeliveryLayout && appConfig?.LOCATION_DATA?.AllowWithNoDocument"
                        :class="selectedFiscalDocType === FiscalDocTypeEnum.NONE ? 'flex-grow-1' : 'flex-grow-0'"
                        :disabled="noFiscalDocumentDisabled"
                        :style="selectedFiscalDocType === FiscalDocTypeEnum.NONE ? 'opacity: 1;' : 'opacity: 0.35;'"
                        :value="FiscalDocTypeEnum.NONE"
                        class="elevation-0 white--text"
                        color="purple"
                        style="height: 40px; border: 0;"
                        @click="onSelectedPaymentTypeChange(FiscalDocTypeEnum.NONE)"
                      >
                        <v-icon
                          color="white"
                          size="18"
                        >
                          mdi-receipt-text-remove
                        </v-icon>

                        <template v-if="selectedFiscalDocType === FiscalDocTypeEnum.NONE">
                          <span class="ml-2">
                            {{ $t('Account.Billing.Field.IsReceiptToggle.None') }}
                            this is a test
                          </span>
                        </template>
                      </v-btn>
                    </v-btn-toggle>
                  </div>

                  <div>
                    <v-btn-toggle
                      v-model="selectedReceiptNotify"
                      :disabled="selectedReceiptNotifyDisabled"
                      class="elevation-0 ma-0-f d-flex"
                      color="success"
                      dense
                      multiple
                    >
                      <v-btn
                        v-if="!useDeliveryLayout"
                        :color="selectedReceiptNotify.includes(NotifyTypeEnum.PRINT) ? 'success' : ''"
                        :disabled="selectedReceiptNotifyDisabled"
                        class="flex-grow-1"
                        text
                        value="print"
                      >
                        <v-icon
                          color="success"
                          left
                          size="22"
                        >
                          mdi-printer-pos-outline
                        </v-icon>

                        {{ $t('Settings.Documents.OrderReceipt.Notify.Print').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :color="selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL) ? 'primary' : ''"
                        :disabled="selectedReceiptNotifyDisabled || !appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)"
                        class="flex-grow-1 justify-start"
                        text
                        value="email"
                      >
                        <div class="flex-grow-1 mt-1">
                          <v-icon
                            class="mb-1"
                            color="primary"
                            left
                            size="18"
                          >
                            mdi-email-arrow-right-outline
                          </v-icon>

                          {{ $t('Settings.Documents.OrderReceipt.Notify.Email').toLocaleUpperCase($i18n.locale) }}
                          <template v-if="selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL)">
                            ({{ selectedReceiptNotifyEmails.length > 0 ? selectedReceiptNotifyEmails.length : hasCustomerWithEmail ? 1 : 0 }})
                          </template>
                        </div>

                        <div
                          v-if="selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL)"
                          class="mt-auto"
                        >
                          <v-btn
                            :disabled="!selectedReceiptNotify.includes(NotifyTypeEnum.EMAIL)"
                            icon
                            small
                            @click.stop="showEmailAddressesDialog"
                          >
                            <v-icon>
                              mdi-plus-box
                            </v-icon>
                          </v-btn>
                        </div>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </div>
              </template>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <customer-email-select-dialog
      :data="customerSelectEmailDialogData"
      :visible.sync="customerSelectEmailDialogVisible"
      @save:emails="onSaveEmailAddresses"
      @cancel:emails="onCancelEmailAddresses"
    />

    <select-payment-terminal-dialog
      :visible.sync="selectPaymentTerminalDialogVisible"
      @terminal:selected="onPaymentTerminalSelected"
    />

    <confirm-dialog
      :html-content="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Content')"
      :html-title="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Title')"
      :info-bar="true"
      :info-bar-body="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmRefund.Notification')"
      :visible.sync="confirmDialogRefundVisible"
      :width="560"
      info-bar-class="purple lighten-2"
      no-button-class="v-btn--outline grey--text"
      title-class="purple white--text lighten-0"
      yes-button-class="purple white--text"
      yes-button-event="on-confirm-refund"
      @on-confirm-refund="onRefundTransaction"
    />

    <confirm-dialog
      :html-content="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Content') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Content')"
      :html-title="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Title') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Title')"
      :info-bar="isManualAcceptedTransaction(selectedTransaction) ? !!$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Notification') : !!$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Notification')"
      :info-bar-body="isManualAcceptedTransaction(selectedTransaction) ? $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAcceptRevert.Notification') : $t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmManualAccept.Notification')"
      :visible.sync="confirmDialogManualAcceptTransVisible"
      :width="580"
      info-bar-class="red lighten-2 white--text"
      no-button-class="v-btn--outline grey--text"
      title-class="orange white--text lighten-0"
      yes-button-class="orange white--text"
      yes-button-event="on-confirm-manual-accept"
      @on-confirm-manual-accept="onManualAcceptCardTransaction"
    />

    <confirm-dialog
      :html-content="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmChangePaymentType.Content', {fromPaymentType: getPaymentTypeLangByCode(selectedTransaction?.PaymentType)?.Title || '', toPaymentType: getPaymentTypeLangByCode(selectedPaymentType)?.Title || ''})"
      :html-title="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmChangePaymentType.Title')"
      :info-bar="!!$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmChangePaymentType.Notification')"
      :info-bar-body="$t('Restaurant.Tables.Dialog.Payment.Transactions.Dialog.ConfirmChangePaymentType.Notification')"
      :visible.sync="confirmDialogChangePaymentTypeVisible"
      :width="580"
      info-bar-class="red lighten-2 white--text"
      no-button-class="v-btn--outline grey--text"
      title-class="orange white--text lighten-0"
      yes-button-class="orange white--text"
      yes-button-event="on-confirm-convert"
      @on-confirm-convert="changeTransactionPaymentType"
    />

    <select-payment-method-dialog
      v-model="selectedPaymentType"
      :payment-methods="paymentTypesForDeliveryStaff"
      :visible.sync="selectPaymentMethodDialogVisible"
      @select="onDeliveryPaymentMethodSelected"
    />
  </v-container>
</template>

<script>
import vModel from '@/mixins/vModel'
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import OrderPaymentType from '@/mixins/orders/paymentType'
import NumPad from '@/components/common/NumPad'
import Translatable from '@/mixins/translatable'
import SelectPaymentTerminalDialog from '@/components/payments/orders/SelectPaymentTerminalDialog'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import {clone, emailIsGuest} from '@/lib/helper/helper'
import CustomerEmailSelectDialog from '@/components/customers/CustomerEmailSelectDialog'
import TerminalsCommon from '@/mixins/payments/terminalsCommon'
import {getContrastColor} from '@/lib/color'
import CashPaymentsCommon from '@/mixins/payments/paymentsCommon'
import PinManagerEnum from '@/api/Enums/PinManagerEnum.js'
import TerminalTransactionResultEnum from '@/api/Enums/TerminalTransactionResultEnum.js'
import TerminalTransactionStatusEnum from '@/api/Enums/TerminalTransactionStatusEnum.js'
import TransactionsPolling from '@/mixins/payments/transactionsPolling.js'
import PaymentMethodSelector from '@/components/payments/PaymentMethodSelector.vue'
import SelectPaymentMethodDialog from '@/components/payments/SelectPaymentMethodDialog.vue'
import TransactionsCommon from '@/mixins/payments/transactionsCommon.js'
import {getDailyOrder} from '@/api/SocketOrderHelper.js'

export default {
  name      : 'OrderPayment',
  components: {
    SelectPaymentMethodDialog,
    PaymentMethodSelector,
    CustomerEmailSelectDialog,
    ConfirmDialog,
    SelectPaymentTerminalDialog,
    NumPad
  },
  mixins: [Auth, AppData, vModel, TransactionsPolling, OrderPaymentType, TerminalsCommon, CashPaymentsCommon, TransactionsCommon, Translatable],
  props : {
    order: {
      type   : Object,
      default: undefined
    },
    orderId: {
      type   : [Number, String],
      default: null
    },
    cartId: {
      type   : [Number, String],
      default: null
    },
    customerId: {
      type   : [Number, String],
      default: null
    },
    orderCustomerEmail: {
      type   : String,
      default: ''
    },
    orderTotal: {
      type   : Number,
      default: 0
    },
    fiscalDocumentMark: {
      type   : [Number, String],
      default: null
    },
    hasFiscalDocument: {
      type   : Boolean,
      default: false
    },
    receipt: {
      type   : Boolean,
      default: false
    },
    receiptType: {
      type   : String,
      default: FiscalDocTypeEnum.RECEIPT
    },
    receiptData: {
      type   : Object,
      default: null
    },
    receiptNotify: {
      type   : Array,
      default: () => []
    },
    receiptNotifyEmails: {
      type   : Array,
      default: () => []
    },
    paymentType: {
      type   : String,
      default: PaymentTypeEnum.CASH
    },
    items: {
      type   : Array,
      default: () => []
    },
    tipAmount: {
      type   : [String, Number],
      default: 0
    }
  },
  DataStore: {posCart: 'cart'},
  data() {
    return {
      numPadModel                            : 0,
      confirmDialogRefundVisible             : false,
      selectPaymentMethodDialogVisible       : false,
      confirmDialogManualAcceptTransVisible  : false,
      confirmDialogChangePaymentTypeVisible  : false,
      selectedPaymentType                    : PaymentTypeEnum.CASH,
      selectedTransaction                    : null,
      selectedTerminal                       : null,
      numPadDisabled                         : false,
      selectedReceiptNotifyDisabled          : false,
      selectPaymentTerminalDialogVisible     : false,
      customerSelectEmailDialogVisible       : false,
      customerSelectEmailDialogData          : null,
      loading                                : false,
      activeReceiptType                      : null,
      activeInvoiceType                      : null,
      transactionAddRemoveLoadersTimeoutValue: 650,
      transactionAddRemoveLoadersTimeout     : null
    }
  },
  computed: {
    NotifyTypeEnum() {
      return NotifyTypeEnum
    },
    PaymentTypeEnum() {
      return PaymentTypeEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },

    useDeliveryLayout() {
      return this.userIsDelivery || this.order?.ReceiptsCount > 0
    },

    transactions() {
      return this.vModel || []
    },

    hasCustomerWithEmail() {
      return this.customerEmail && !emailIsGuest(this.customerEmail)
    },

    customerEmail() {
      return this.posCart?.SCustomer?.Email || this.orderCustomerEmail || ''
    },

    selectedFiscalDocTypeName() {

      if (this.appHasReceiptSaleDocEnabled && this.selectedFiscalDocType === FiscalDocTypeEnum.RECEIPT) return this.$t('Account.Billing.Field.IsReceiptToggle.ReceiptSale')
      if (this.appHasReceiptServiceDocEnabled && this.selectedFiscalDocType === FiscalDocTypeEnum.SERVICE_RECEIPT) return this.$t('Account.Billing.Field.IsReceiptToggle.ReceiptService')
      if (this.appHasReceiptCollectionDocEnabled && this.selectedFiscalDocType === FiscalDocTypeEnum.COLLECTION_RECEIPT) return this.$t('Account.Billing.Field.IsReceiptToggle.ReceiptCollection')
      if (this.appHasReceiptSelfSupplyDocEnabled && this.selectedFiscalDocType === FiscalDocTypeEnum.RECEIPT_SELF_SUPPLY) return this.$t('Account.Billing.Field.IsReceiptToggle.ReceiptSelfSupply')
      if (this.appHasReceiptSelfUseDocEnabled && this.selectedFiscalDocType === FiscalDocTypeEnum.RECEIPT_SELF_USE) return this.$t('Account.Billing.Field.IsReceiptToggle.ReceiptSelfUse')

      return this.$t('Account.Billing.Field.IsReceiptToggle.Receipt')
    },

    selectedFiscalDocType: {
      get() {
        return this.receiptType || ''
      },
      set(val) {
        this.$emit('update:receiptType', val)
      }
    },

    selectedReceiptData: {
      get() {
        return this.receiptData || null
      },
      set(val) {
        this.$emit('update:receiptData', val)
      }
    },

    selectedReceiptNotify: {
      get() {
        return this.receiptNotify || []
      },
      set(val) {
        this.$emit('update:receiptNotify', val)
      }
    },

    selectedReceiptNotifyEmails: {
      get() {
        return this.receiptNotifyEmails || []
      },
      set(val) {
        this.$emit('update:receiptNotifyEmails', val)
      }
    },

    hasCashTransaction() {
      return this.transactions.some(transaction => transaction.PaymentType === PaymentTypeEnum.CASH)
    },

    canAddTransaction() {
      const orderBalance = parseFloat(this.orderBalance.toFixed(2))
      const numPadModel = parseFloat(this.numPadModel.toFixed(2))

      return orderBalance > 0 && numPadModel > 0 && (this.maxTransactionAmount > 0 ? (numPadModel <= orderBalance && orderBalance - numPadModel >= 0) : true) &&
          ((this.selectedPaymentType === PaymentTypeEnum.CASH && this.isValidAmountForCashPayment) || this.selectedPaymentType !== PaymentTypeEnum.CASH)
    },

    transactionsTotal() {
      const val = this.transactions.reduce((a, b) => a + parseFloat(b.Amount), 0) || 0
      return parseFloat(val.toFixed(2))
    },

    transactionsReceivedTotal() {
      const val = this.transactions.reduce((a, b) => a + parseFloat(b.Received), 0) || 0
      return parseFloat(val.toFixed(2))
    },

    transactionsPaidTotal() {
      let val = this.transactions.reduce((a, b) => a + parseFloat(b.Amount), 0) || 0

      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        val = this.transactions.filter(transaction => this.eftPosCardTransactionPaidSuccessful(transaction) || !this.isEftPosEnabledTransaction(transaction))?.reduce((a, b) => a + parseFloat(b.Amount), 0) || 0
      }

      return parseFloat(val.toFixed(2))
    },

    initValue() {
      return this.orderBalance > 0 ? this.orderBalance : 0
    },

    orderBalance() {
      const orderTotal = parseFloat(this.orderTotal.toFixed(2))
      return orderTotal - this.transactionsTotal
    },

    orderChange() {
      const orderTotal = parseFloat(this.orderTotal.toFixed(2))
      return Math.abs(this.transactionsReceivedTotal > orderTotal ? orderTotal - this.transactionsReceivedTotal : 0)
    },

    maxTransactionAmount() {
      return this.selectedPaymentType === PaymentTypeEnum.CASH ? undefined : this.orderBalance
    },

    tablePaidBalance() {
      const orderTotal = parseFloat(this.orderTotal.toFixed(2))
      return orderTotal - this.transactionsPaidTotal
    }
  },
  watch: {
    selectedFiscalDocType: {
      immediate: true,
      handler(val) {
        if (val === FiscalDocTypeEnum.NONE) {
          this.setSelectedPaymentType(PaymentTypeEnum.requiresFiscalDocument(this.selectedPaymentType) ? PaymentTypeEnum.CASH : this.selectedPaymentType)
          this.selectedReceiptNotifyDisabled = true
          this.selectedReceiptNotify = []
        }

        if (FiscalDocTypeEnum.isReceipt(val)) {
          if (this.selectedPaymentType === PaymentTypeEnum.DELIVERY) this.setSelectedPaymentType(PaymentTypeEnum.CASH)
          if (FiscalDocTypeEnum.isSelfDoc(val)) this.setSelectedPaymentType(PaymentTypeEnum.SELF_DELIVERY)
          this.selectedReceiptNotify = clone(this.ReceiptsNotify)?.filter(item => this.hasCustomerWithEmail ? true : item !== NotifyTypeEnum.EMAIL) || []
          this.selectedReceiptNotifyDisabled = false
        }

        if (FiscalDocTypeEnum.isInvoice(val)) {
          if (this.selectedPaymentType === PaymentTypeEnum.DELIVERY) this.setSelectedPaymentType(PaymentTypeEnum.CASH)
          this.selectedReceiptNotify = clone(this.ReceiptsInvoiceNotify)?.filter(item => this.hasCustomerWithEmail ? true : item !== NotifyTypeEnum.EMAIL) || []
          this.selectedReceiptNotifyDisabled = false
        }

        if (!FiscalDocTypeEnum.isSelfDoc(val) && this.selectedPaymentType === PaymentTypeEnum.SELF_DELIVERY) {
          this.setSelectedPaymentType(PaymentTypeEnum.CASH)
        }

        this.checkCashPaymentsAllowed()
      }
    },

    selectedPaymentType(val) {
      if (PaymentTypeEnum.requiresFiscalDocument(val) && this.selectedFiscalDocType === FiscalDocTypeEnum.NONE) this.selectedFiscalDocType = this.activeReceiptType || this.activeInvoiceType || null

      /*
      if (!FiscalDocTypeEnum.isSelfDoc(this.selectedFiscalDocType) && val === PaymentTypeEnum.SELF_DELIVERY) {
        this.selectedFiscalDocType = this.activeReceiptType = FiscalDocTypeEnum.RECEIPT_SELF_SUPPLY
      }
       */
    },

    selectedReceiptNotify(newVal) {
      if (newVal.includes(NotifyTypeEnum.EMAIL) && !this.selectedReceiptNotifyEmails?.length && !this.hasCustomerWithEmail) {
        this.showEmailAddressesDialog()
      }
    },

    selectedReceiptNotifyEmails(newVal) {
      if (!newVal?.length && !this.hasCustomerWithEmail) {
        this.onCancelEmailAddresses()
      }
    },

    busy(newVal) {
      this.loading = newVal
    }
  },
  created() {
    this.initFiscalDocs()

    this.setSelectedPaymentType(this.paymentType || PaymentTypeEnum.CASH)

    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.Add, this.onAddTransactionResponse)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.Remove, this.onRemoveTransactionResponse)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.Sale, this.onTerminalSaleResponse)
    this.$bus.$on(window.SocketCommand.Staff.Delivery.User.EftPos.Sale, this.onTerminalSaleResponse)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.Refund, this.onTerminalRefundResponse)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.CartResult, this.onTerminalTransactionResult)
    this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.ManualAccept, this.onManualAcceptCardTransactionResponse)
    this.$bus.$on('hide-app-offline-modal', this.onConnectionRestore)
    this.$bus.$on('email-notify-dialog:visible', this.disableOrEnableNumpad)
    this.$bus.$on('customer-company-select-dialog:visible', this.disableOrEnableNumpad)
    this.$bus.$on('invoice-company:selected', this.onInvoiceCompanySelected)
    this.$bus.$on('invoice-company:canceled', this.onInvoiceCompanyCanceled)

    this.initTransactionBusy(this.transactions)
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.Add, this.onAddTransactionResponse)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.Remove, this.onRemoveTransactionResponse)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.Sale, this.onTerminalSaleResponse)
    this.$bus.$off(window.SocketCommand.Staff.Delivery.User.EftPos.Sale, this.onTerminalSaleResponse)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.Refund, this.onTerminalRefundResponse)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.CartResult, this.onTerminalTransactionResult)
    this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.ManualAccept, this.onManualAcceptCardTransactionResponse)
    this.$bus.$off('hide-app-offline-modal', this.onConnectionRestore)
    this.$bus.$off('email-notify-dialog:visible', this.disableOrEnableNumpad)
    this.$bus.$off('customer-company-select-dialog:visible', this.disableOrEnableNumpad)
    this.$bus.$off('invoice-company:selected', this.onInvoiceCompanySelected)
    this.$bus.$off('invoice-company:canceled', this.onInvoiceCompanyCanceled)
  },
  methods: {
    getContrastColor,

    initFiscalDocs() {

      if (this.appHasReceiptSaleDocEnabled && this.appHasReceiptServiceDocEnabled) {
        this.activeReceiptType = FiscalDocTypeEnum.RECEIPT
      } else if (this.appHasReceiptSaleDocEnabled) {
        this.activeReceiptType = FiscalDocTypeEnum.RECEIPT
      } else if (this.appHasReceiptServiceDocEnabled) {
        this.activeReceiptType = FiscalDocTypeEnum.SERVICE_RECEIPT
      }

      if (this.appHasInvoiceSaleDocEnabled && this.appHasInvoiceServiceDocEnabled) {
        this.activeInvoiceType = FiscalDocTypeEnum.INVOICE
      } else if (this.appHasInvoiceSaleDocEnabled) {
        this.activeInvoiceType = FiscalDocTypeEnum.INVOICE
      } else if (this.appHasInvoiceServiceDocEnabled) {
        this.activeInvoiceType = FiscalDocTypeEnum.SERVICE_INVOICE
      }

    },

    onConnectionRestore() {
      this.$bus.$emit('cart:cart-update', 'app-connection-restore')
      this.$bus.$emit('add-payment-loader-stop')
    },

    showEmailAddressesDialog() {
      this.customerSelectEmailDialogData = {
        receiptId: null,
        order    : {
          Email: this.customerEmail
        },
        data: {
          EmailAddresses: [...this.selectedReceiptNotifyEmails]
        }
      }
      this.customerSelectEmailDialogVisible = true
    },

    onSaveEmailAddresses(payload) {
      this.$set(this, 'selectedReceiptNotifyEmails', clone(payload?.data?.EmailAddresses || []))
    },

    onCancelEmailAddresses() {
      const index = this.selectedReceiptNotify?.findIndex(item => item === NotifyTypeEnum.EMAIL)
      if (index > -1) this.selectedReceiptNotify.splice(index, 1)
    },

    setNumPadValue(val) {
      this.numPadModel = val
    },

    onNumPadEnter() {
      this.onAddTransaction()
    },

    onSelectedPaymentTypeChange(val) {
      if (FiscalDocTypeEnum.isInvoice(val)) {
        this.$bus.$emit('fiscal-document:create', {CustomerId: this.customerId}, val, this.selectedReceiptData)
        this.numPadDisabled = true
      } else {
        this.selectedReceiptData = null
        this.numPadDisabled = false
      }
    },

    disableOrEnableNumpad(val) {
      this.numPadDisabled = val
    },

    onInvoiceCompanySelected(company) {
      this.selectedReceiptData = company
      this.numPadDisabled = false
    },

    onInvoiceCompanyCanceled() {
      this.numPadDisabled = false
    },

    getTransactionPaymentTypeName(transaction) {
      const retVal = this.getPaymentTypeLangByCode(transaction?.PaymentType)?.Title || transaction?.name || ''

      return retVal.toLocaleUpperCase(this.$i18n.locale)
    },

    getTransactionInfo(transaction) {
      if (transaction?.WaitingPos) {
        return {
          Type   : 'info',
          Icon   : 'mdi-information',
          Color  : 'blue',
          Message: transaction?.Action && this.getTxnType(transaction.Action) ? `<strong>${this.getTxnType(transaction.Action)?.Name}</strong> - ${this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SaleWaitingResult')}` : this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SaleWaitingResult')
        }
      }

      if (this.getTransactionResult(transaction.Status)) {
        return {
          Type   : 'info',
          Icon   : this.getTransactionResult(transaction.Status)?.Icon,
          Color  : this.getTransactionResult(transaction.Status)?.Color,
          Message: `<strong>${this.getTxnType(transaction.TxnType)?.Name || ''} ${this.getTransactionResult(transaction.Status)?.Name}</strong> - ${this.getTransactionResult(transaction.Status)?.Tooltip}`
        }
      }

      if (!transaction?.Refund && this.eftPosCardTransactionPaidSuccessful(transaction)) {
        return {
          Type   : 'info',
          Icon   : 'mdi-information',
          Color  : 'blue',
          Message: this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.RefundError')
        }
      }

      if (transaction?.Refund && transaction?.Refund === 'fail') {
        return {
          Type   : 'error',
          Icon   : 'mdi-alert-circle',
          Color  : 'red',
          Message: `<strong>${this.getTxnType('REFUND')?.Name}</strong> - ${this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.RefundError')}`
        }
      }

      if (this.eftPosCardTransactionRefundedSuccessful(transaction)) {
        return {
          Type   : 'success',
          Icon   : 'mdi-information',
          Color  : 'green',
          Message: `<strong>${this.getTxnType('REFUND')?.Name}</strong> - ${this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.RefundSuccess')}`
        }
      }

      if (!transaction?.Paid) {
        return {
          Type   : 'info',
          Icon   : 'mdi-information',
          Color  : 'blue-grey',
          Message: this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.Sale')
        }
      }

      if (transaction?.Paid && transaction?.Paid === 'fail') {
        return {
          Type   : 'error',
          Icon   : 'mdi-alert-circle',
          Color  : 'red',
          Message: `<strong>${this.getTxnType('SALE')?.Name}</strong> - ${this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SaleError')}`
        }
      }

      if (this.eftPosCardTransactionPaidSuccessful(transaction)) {
        return {
          Type   : 'success',
          Icon   : 'mdi-information',
          Color  : 'green',
          Message: `<strong>${this.getTxnType('SALE')?.Name}</strong> - ${this.getTransactionResult(transaction.Status)?.Tooltip || this.$t('Restaurant.Tables.Dialog.Payment.Transactions.Info.SaleSuccess')}`
        }
      }

      return {
        Type   : 'info',
        Icon   : this.getTransactionResult(transaction.Status)?.Icon || 'mdi-information',
        Color  : this.getTransactionResult(transaction.Status)?.Color || 'blue',
        Message: this.getTransactionResult(transaction.Status)?.Tooltip || ''
      }
    },

    executeDefaultAction(transaction) {
      if (this.loading || this.busy || transaction.busy || transaction.WaitingPos || !this.isEftPosEnabledTransaction(transaction) || !this.appHasFiscalDocsProvider || this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD || !this.appHasFiscalDocsEnabled) return

      if (!transaction.Paid) {
        this.showSelectPaymentTerminalDialog(transaction)
        return
      }

      if (transaction.Paid === 'fail' && transaction.Data) {
        this.onRetryTransaction(transaction)
        return
      }

      if ((PaymentTypeEnum.isCard(transaction.PaymentType) && this.eftPosCardTransactionPaidSuccessful(transaction) && !this.useDeliveryLayout) || (PaymentTypeEnum.isDeliveryCard(transaction.PaymentType) && this.eftPosCardTransactionPaidSuccessful(transaction) && this.useDeliveryLayout && this.posUser.Settings?.DeliveryStaff?.Permissions?.CanTransactionRefund)) {
        this.showConfirmRefundTransactionDialog(transaction)

      }
    },

    onAddTransaction() {
      if (!this.canAddTransaction || this.loading) return

      this.loading = true
      this.$bus.$emit('add-payment-loader-start')

      const amount = parseFloat(this.numPadModel.toFixed(2))
      const paymentType = this.selectedPaymentType

      const payload = {
        OrderId      : this.orderId || null,
        CartId       : this.cartId || null,
        TransactionId: Date.now().toString() + (this.transactions.length + 1),
        PaymentType  : paymentType,
        Received     : amount,
        Amount       : amount > this.orderBalance ? this.orderBalance : amount
      }

      if (this.$route.name === 'Pos') {
        this.$bus.$emit('cart:cart-add-transaction', payload)
      } else {
        window.callAS(window.SocketCommand.Order.Payment.Transaction.Add, payload)
      }

      this.$set(payload, 'busy', true)
      this.transactions.push(payload)
      this.numPadModel = this.orderBalance
    },
    onAddTransactionResponse(data, payload) {
      clearTimeout(this.transactionAddRemoveLoadersTimeout)
      this.transactionAddRemoveLoadersTimeout = setTimeout(() => {
        this.loading = false
        this.$bus.$emit('add-payment-loader-stop')
      }, this.transactionAddRemoveLoadersTimeoutValue)

      const transaction = this.transactions?.find(t => t.TransactionId === payload.TransactionId)
      setTimeout(() => {
        this.setTransactionBusy(transaction, false)
      }, this.$route.name === 'OrdersToday' ? 2500 : 0)


      const transactionIndex = this.transactions?.findIndex(t => t.TransactionId === payload.TransactionId)

      if ((data?.error || data.status === 'fail') && transactionIndex > -1) {
        this.transactions.splice(transactionIndex, 1)
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3500
        })

        if (this.$route.name === 'OrdersToday') {
          getDailyOrder(payload?.OrderId)
        } else {
          this.$bus.$emit('cart:cart-update', 'order-payment-add-transaction-error')
        }

        return
      }

      if (data.TransactionId && transactionIndex > -1) {
        this.transactions[transactionIndex].TransactionId = data.TransactionId
      }

      this.$bus.$emit('cds:send-message', {
        Action : 'cds:payment-change',
        Payload: {
          Change  : this.orderChange || 0,
          Received: this.transactionsReceivedTotal || 0
        }
      })

      this.$bus.$emit('cart:cart-update', window.SocketCommand.Order.Payment.Transaction.Add)
    },

    canManualAcceptCardTransaction(transaction) {
      if (!transaction || !this.posUser?.Settings?.AllUsers?.Permissions?.CanTransactionManualAccept || !this.isEftPosEnabledTransaction(transaction) || this.appFiscalDocsProviderType !== FiscalDocsProviderTypeEnum.CLOUD) return false

      const activeForAllStates = this.posUser?.Settings?.AllUsers?.Permissions?.CanTransactionManualAcceptAllStates ?? false

      return (PaymentTypeEnum.isCard(transaction.PaymentType)
              && transaction.Action === 'SALE'
              && !transaction.busy
              && (!transaction.Paid || transaction.Paid === 'fail')
              && transaction.ResponseIntentResult !== 'COMPLETED'
              && transaction.ResponseIntentStatus !== 'FAILED')

          || (PaymentTypeEnum.isCard(transaction.PaymentType)
              && transaction.Action === 'SALE'
              && !transaction.busy
              && (!transaction.Paid || transaction.Paid === 'fail')
              && transaction.ResponseIntentStatus === 'JOB_MAX_ATTEMPTS_EXCEEDED')

          || (PaymentTypeEnum.isCard(transaction.PaymentType)
              && activeForAllStates
              && transaction.Action === 'SALE'
              && !transaction.busy
              && (!transaction.Paid || transaction.Paid === 'fail')
              && transaction.ResponseIntentStatus !== 'APPROVED')

          || (PaymentTypeEnum.isCard(transaction.PaymentType)
              && this.isManualAcceptedTransaction(transaction))
    },

    isManualAcceptedTransaction(transaction) {
      return !!transaction?.UserConfirmedAt
    },

    showConfirmManualAcceptCardTransactionDialog(transaction) {
      if (!this.canManualAcceptCardTransaction(transaction)) return

      this.selectedTransaction = transaction

      this.executeWithPinPermissionManager(PinManagerEnum.PosTransactionManualAccept, (success) => {
        if (!success) return

        this.confirmDialogManualAcceptTransVisible = true
      })
    },

    onManualAcceptCardTransaction() {
      if (!this.selectedTransaction || !this.canManualAcceptCardTransaction(this.selectedTransaction)) return

      this.setTransactionBusy(this.selectedTransaction, true)
      this.$bus.$emit('add-payment-loader-start')

      const payload = {
        OrderId      : this.orderId || null,
        CartId       : this.cartId || null,
        Revert       : this.isManualAcceptedTransaction(this.selectedTransaction),
        TransactionId: this.selectedTransaction?.TransactionId
      }

      window.callAS(window.SocketCommand.Order.Payment.Transaction.ManualAccept, payload)
    },
    onManualAcceptCardTransactionResponse(data, payload) {
      if (data.status === 'fail') {
        const transaction = this.transactions?.find(t => t.TransactionId === payload.TransactionId)
        this.setTransactionBusy(transaction, false)
        return
      }

      const transactionIndex = this.transactions?.findIndex(t => t.TransactionId === payload.TransactionId)
      if (data.TransactionId && transactionIndex > -1) {
        this.transactions[transactionIndex].TransactionId = data.TransactionId
      }
    },

    canRemoveTransaction(transaction) {
      if (!transaction || PaymentTypeEnum.ONLINE_CARDS.includes(transaction.PaymentType)) return false

      if (this.isEftPosEnabledTransaction(transaction) && this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        return !transaction.Paid || transaction.Paid === 'fail'
      }

      return true
    },

    onRemoveTransaction(transaction) {
      if (!this.canRemoveTransaction(transaction)) return

      this.loading = true
      this.$bus.$emit('add-payment-loader-start')
      this.$set(transaction, 'busy', true)

      this.numPadModel = this.orderBalance

      const payload = {
        OrderId      : this.orderId || null,
        CartId       : this.cartId || null,
        TransactionId: transaction?.TransactionId
      }

      if (this.$route.name === 'Pos') {
        this.$bus.$emit('cart:cart-remove-transaction', payload)
      } else {
        window.callAS(window.SocketCommand.Order.Payment.Transaction.Remove, payload)
      }
    },
    onRemoveTransactionResponse(data, payload) {
      clearTimeout(this.transactionAddRemoveLoadersTimeout)
      this.transactionAddRemoveLoadersTimeout = setTimeout(() => {
        this.loading = false
        this.$bus.$emit('add-payment-loader-stop')
      }, this.transactionAddRemoveLoadersTimeoutValue)
      this.stopTransactionsPolling()

      if (data.status === 'fail') {
        const transaction = this.transactions?.find(t => t.TransactionId === payload.TransactionId)
        this.setTransactionBusy(transaction, false)
        return
      }

      const transactionIndex = this.transactions?.findIndex(t => t.TransactionId === payload.TransactionId)
      this.transactions.splice(transactionIndex, 1)

      this.$bus.$emit('cds:send-message', {
        Action : 'cds:payment-change',
        Payload: {
          Change  : this.orderChange || 0,
          Received: this.transactionsReceivedTotal || 0
        }
      })

      this.$bus.$emit('cart:cart-update', window.SocketCommand.Order.Payment.Transaction.Remove)
    },

    onRetryTransaction(transaction) {
      if (PaymentTypeEnum.isPosIris(transaction)) return

      this.$bus.$emit('add-payment-loader-start')
      this.setTransactionBusy(transaction, true)

      transaction.WaitingPos = true
      transaction.Action = 'SALE'

      const payload = {
        OrderId           : this.orderId || null,
        CartId            : this.cartId || null,
        TerminalId        : transaction?.TerminalId,
        TerminalVendorId  : transaction?.TerminalVendorId,
        TransactionId     : transaction?.TransactionId,
        ReceiptType       : transaction?.Data?.ReceiptType,
        ReceiptData       : transaction?.Data?.ReceiptData,
        FiscalDocumentMark: this.fiscalDocumentMark || null,
        TipAmount         : transaction?.Data?.TipAmount || 0,
        Instalments       : transaction?.Data?.Instalments || 0,
        Items             : transaction?.Data?.Items || []
      }

      if (this.useDeliveryLayout) {
        window.callAS(window.SocketCommand.Staff.Delivery.User.EftPos.Sale, payload, window.SocketCommand.Staff.Delivery.User.EftPos.Sale, this.getWebEcrTimeout(payload.TerminalVendorId))
      } else {
        window.callAS(window.SocketCommand.Order.Payment.Transaction.Sale, payload, window.SocketCommand.Order.Payment.Transaction.Sale, this.getWebEcrTimeout(payload.TerminalVendorId))
      }
    },

    showConfirmRefundTransactionDialog(transaction) {
      if (transaction.WaitingPos || this.loading || this.busy || transaction.busy || !transaction.OrderCode) return

      this.selectedTransaction = transaction
      this.executeWithPinPermissionManager(PinManagerEnum.TransactionRefund, (success) => {
        if (!success) return

        this.confirmDialogRefundVisible = true
      })
    },

    onRefundTransaction() {
      if (!this.selectedTransaction) return

      this.$bus.$emit('add-payment-loader-start')
      this.setTransactionBusy(this.selectedTransaction, true)
      this.selectedTransaction.WaitingPos = true
      this.selectedTransaction.Action = 'REFUND'

      const payload = {
        OrderId      : this.orderId || null,
        CartId       : this.cartId || null,
        TransactionId: this.selectedTransaction?.TransactionId
      }

      if (this.useDeliveryLayout) {
        window.callAS(window.SocketCommand.Staff.Delivery.User.EftPos.Refund, payload, window.SocketCommand.Staff.Delivery.User.EftPos.Refund, this.getWebEcrTimeout(this.selectedTransaction?.TerminalVendorId))
      } else {
        window.callAS(window.SocketCommand.Order.Payment.Transaction.Refund, payload, window.SocketCommand.Order.Payment.Transaction.Refund, this.getWebEcrTimeout(this.selectedTransaction?.TerminalVendorId))
      }
    },

    deliverySelectPaymentMethod(transaction) {
      if (!transaction) return

      this.selectedTransaction = transaction
      this.selectPaymentMethodDialogVisible = true
    },
    onDeliveryPaymentMethodSelected(paymentMethod) {
      if (!paymentMethod) return

      this.selectedPaymentType = paymentMethod.code
      this.confirmChangeTransactionPaymentType()
    },
    confirmChangeTransactionPaymentType() {
      this.confirmDialogChangePaymentTypeVisible = true
    },
    changeTransactionPaymentType() {
      const selectedTransaction = this.selectedTransaction || null
      const selectedPaymentType = this.selectedPaymentType || null

      if (!selectedTransaction || !selectedPaymentType || (PaymentTypeEnum.isDeliveryCard(selectedTransaction.PaymentType) && this.eftPosCardTransactionPaidSuccessful(selectedTransaction))) return

      this.setTransactionBusy(selectedTransaction, true)

      const payload = {
        TransactionId: selectedTransaction.TransactionId || null,
        PaymentType  : selectedPaymentType || null,
        OrderId      : selectedTransaction.OrderId || this.orderId || null
      }

      window.callAS(window.SocketCommand.Order.Payment.Transaction.ChangePaymentType, payload)
    },

    showSelectPaymentTerminalDialog(transaction) {
      this.selectedTransaction = transaction
      this.selectPaymentTerminalDialogVisible = true
    },
    onPaymentTerminalSelected(terminal, instalments = 0) {
      this.selectedTerminal = terminal

      const transactionTerminalInUse = this.transactions?.some(t => t.TerminalId === terminal.TerminalId && t.Status !== 'APPROVED' && t.WaitingPos)
      this.$set(this.selectedTransaction, 'TerminalId', terminal.TerminalId)

      if (transactionTerminalInUse) {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Restaurant.Tables.Dialog.SelectTerminal.Notification.Error'),
          type   : 'warning',
          icon   : 'warning',
          timeout: 4000
        })
      } else {
        this.setTransactionBusy(this.selectedTransaction, true)
        this.selectedTransaction.WaitingPos = true
        this.selectedTransaction.Action = 'SALE'

        this.$set(this.selectedTransaction, 'Nickname', this.selectedTerminal?.Nickname)
        this.$set(this.selectedTransaction, 'TerminalName', this.selectedTerminal?.TerminalName)
        this.$set(this.selectedTransaction, 'TerminalId', this.selectedTerminal?.TerminalId)
        this.$set(this.selectedTransaction, 'TerminalVendorId', this.selectedTerminal?.PosVendorId)
        this.$set(this.selectedTransaction, 'TerminalVendorCode', this.selectedTerminal?.AcquirerCode)

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Restaurant.Tables.Dialog.SelectTerminal.Notification.Success'),
          type: 'info',
          icon: 'info'
        })

        const items = this.items.map(item => {
          return {
            RowId   : item.RowId,
            Quantity: item.UnitId > 1 ? item.UnitQuantity : item.Quantity
          }
        })

        const payload = {
          OrderId           : this.orderId || null,
          CartId            : this.cartId || null,
          TerminalId        : this.selectedTerminal?.TerminalId,
          TerminalVendorId  : this.selectedTerminal?.PosVendorId,
          TransactionId     : this.selectedTransaction?.TransactionId,
          ReceiptType       : this.selectedFiscalDocType || FiscalDocTypeEnum.RECEIPT,
          ReceiptData       : this.selectedReceiptData || null,
          FiscalDocumentMark: this.fiscalDocumentMark || null,
          TipAmount         : this.tipAmount || 0,
          Instalments       : instalments || 0,
          Items             : items
        }

        if (this.useDeliveryLayout) {
          window.callAS(window.SocketCommand.Staff.Delivery.User.EftPos.Sale, payload, window.SocketCommand.Staff.Delivery.User.EftPos.Sale, this.getWebEcrTimeout(payload.TerminalVendorId))
          setTimeout(() => {
            this.$bus.$emit('update-delivery-user-shift-data')
          }, 500)

        } else {
          window.callAS(window.SocketCommand.Order.Payment.Transaction.Sale, payload, window.SocketCommand.Order.Payment.Transaction.Sale, this.getWebEcrTimeout(payload.TerminalVendorId))
        }
        this.$bus.$emit('add-payment-loader-start')
      }
    },

    async onTerminalTransactionResult(response) {
      if (this.$route.name === 'OrdersToday') {
        const transaction = this.transactions?.find(t => t.TransactionId === response.transaction_id)
        this.setTransactionBusy(transaction, false)
      }
    },

    async onTerminalRefundResponse(response, payload) {
      if (response?.status === 'fail') {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.GenericError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    async onTerminalSaleResponse(response, payload) {
      if (response?.status === 'fail') {
        this.$bus.$emit('app-show-notification', {
          body: response?.customErrorMessage ? response.customErrorMessage : this.$t('Payment.Transaction.Error.AnotherRequest'),
          type: 'error',
          icon: 'warning'
        })

        const transaction = this.transactions?.find(t => t.TransactionId === payload.TransactionId)
        this.setTransactionBusy(transaction, false)
        if (transaction) transaction.ResponseIntentResult = 'COMPLETED'
      } else {
        const transactionResult = this.getTransactionResult(response?.ResponseIntentStatus) || this.getTransactionResult(response?.Status)
        const transactionStatus = this.getTransactionStatus(response?.ResponseIntentResult)
        const transaction = this.transactions?.find(t => t.TransactionId === payload.TransactionId)

        if (transactionResult) {
          if (transaction) {
            this.$set(transaction, 'Status', transactionResult.Code)
            this.$set(transaction, 'ResponseIntentResult', transactionStatus.Code)
            this.$set(transaction, 'ResponseIntentStatus', transactionResult.Code)
            transaction.Paid = transactionResult.Code === TerminalTransactionResultEnum.APPROVED.Code ? 'success' : 'fail'
            transaction.WaitingPos = false

            transaction.TerminalId = response.TerminalId
            transaction.TerminalName = response.TerminalName
            transaction.TerminalVendorId = response.TerminalVendorId
            transaction.TerminalVendorCode = response.TerminalVendorCode

            transaction.Data = {}
            transaction.Data.ReceiptType = response.ReceiptType
            transaction.Data.ReceiptData = response.ReceiptData
            transaction.Data.TipAmount = response.TipAmount
            transaction.Data.Instalments = response.Instalments
            transaction.Data.Items = response.Items

            transaction.Items = response.Items

            this.initTransactionBusy([transaction])

            this.launchVivaTerminalAndroidApp(transaction)
          }
        }
      }
      this.$bus.$emit('add-payment-loader-stop')
    },

    setTransactionBusy(transaction, status = true, timeout = this.terminalTransactionBusyTime) {
      if (!transaction) return
      this.clearTransactionBusyTimer(transaction)
      if (status) this.setTransactionBusyTimer(transaction, timeout)
    },

    setTransactionBusyTimer(transaction, timeout = this.terminalTransactionBusyTime) {
      if (!transaction || timeout < 0) return

      transaction.timeout = setTimeout(() => {
        this.$set(transaction, 'busy', false)
        this.$sessionStorage.remove(transaction.TransactionId)
      }, timeout)

      this.$set(transaction, 'busy', true)
      this.$sessionStorage.set(transaction.TransactionId, transaction.timeout)
    },

    clearTransactionBusyTimer(transaction) {
      if (!transaction) return

      const transactionTimeout = this.$sessionStorage.get(transaction.TransactionId, transaction?.timeout ?? null)
      if (transactionTimeout) clearTimeout(transactionTimeout)

      this.$sessionStorage.remove(transaction.TransactionId)
      this.$set(transaction, 'busy', false)
    },

    initTransactionBusy(transactions) {
      if (!Array.isArray(transactions) || !(this.appHasFiscalDocsProvider && this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD)) return

      let hasBusyTransactions = false
      transactions.forEach(transaction => {

        this.clearTransactionBusyTimer(transaction)
        const transactionStatus = this.getTransactionResult(transaction.ResponseIntentStatus)?.Code
        const transactionResult = this.getTransactionStatus(transaction.ResponseIntentResult)?.Code
        const isActionAdd = transaction?.Action === 'ADD'

        if (!isActionAdd && !transaction?.External && (PaymentTypeEnum.isEftPosPaymentType(transaction.PaymentType) || PaymentTypeEnum.isDeliveryCard(transaction.PaymentType)) && (transactionResult === TerminalTransactionStatusEnum.SENT.Code || transactionResult === TerminalTransactionStatusEnum.PENDING.Code || (!transactionResult && !transactionStatus))) {
          this.setTransactionBusyTimer(transaction)
          transaction.WaitingPos = true
          hasBusyTransactions = true
        }

        if (transaction.PaymentType === PaymentTypeEnum.CASH) {
          transaction.WaitingPos = false
        }
      })

      if (!hasBusyTransactions) this.$bus.$emit('add-payment-loader-stop')
    }
  }
}
</script>

<style scoped>
:deep(.v-list-item__action) {
  margin: 0;
}

:deep(.v-alert__icon.v-icon) {
  font-size: 18px;
}

:deep(.v-alert__icon) {
  margin-right: 6px;
}

:deep(.v-badge.invoice-warning-badge .v-badge__wrapper) {
  margin-left: 0;
}
</style>
